import React, { useEffect, useMemo, useState } from "react";
import {
  Alert,
  Card,
  Typography,
  RadioGroup,
  FormControlLabel,
  Radio,
  Divider,
} from "@mui/material";
import LoadingButton from "@mui/lab/LoadingButton";
import { useStripe, useElements, CardElement } from "@stripe/react-stripe-js";
import { useUserInfo } from "../../utils/user";
import { useApi } from "../../utils/api";

const CARD_ELEMENT_OPTIONS = {
  style: {
    base: {
      color: "#32325d",
      fontFamily: "Roboto Mono",
      fontSmoothing: "antialiased",
      fontSize: "16px",
      "::placeholder": { color: "#aab7c4" },
    },
    invalid: { color: "#fa755a", iconColor: "#fa755a" },
  },
};

function normalizeStripeError(err) {
  if (!err) return "Payment failed.";
  if (typeof err === "string") return err;
  return err.message || "Payment failed.";
}

const CheckoutForm = ({
  onSuccess,
  onPartialSuccess,
  userProducts,
  savedPaymentMethods = [],
  defaultPaymentMethodId,
  stripeCustomerId: existingStripeCustomerId,
}) => {
  const {
    coachAccountId,
    id: userId,
    name,
    email,
    stripeCustomerId,
  } = useUserInfo();

  const stripe = useStripe();
  const elements = useElements();

  const {
    createCustomer,
    createSubscription,
    postUserProducts,

    // NEW/UPDATED API methods you need:
    createSetupIntent,
    createOneTimePaymentIntent,

    // Optional: if you keep server-side subscription SCA handling
    // confirmSubscriptionPaymentIntent,
  } = useApi();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const [cachedStripeCustomerId, setCachedStripeCustomerId] = useState(null);

  // Saved PM selection
  const [selectedPaymentMethodId, setSelectedPaymentMethodId] = useState(null);
  const [useNewPaymentMethod, setUseNewPaymentMethod] = useState(
    (savedPaymentMethods || []).length === 0
  );

  useEffect(() => {
    if (savedPaymentMethods?.length > 0) {
      setSelectedPaymentMethodId(
        defaultPaymentMethodId || savedPaymentMethods[0].id
      );
      setUseNewPaymentMethod(false);
    } else {
      setSelectedPaymentMethodId(null);
      setUseNewPaymentMethod(true);
    }
  }, [savedPaymentMethods, defaultPaymentMethodId]);

  const recurringProduct = useMemo(
    () => (userProducts || []).find((p) => p.recurring),
    [userProducts]
  );
  const oneTimeProducts = useMemo(
    () => (userProducts || []).filter((p) => !p.recurring),
    [userProducts]
  );

  async function ensureCustomerId() {
    const fromContext = existingStripeCustomerId || stripeCustomerId;
    if (cachedStripeCustomerId) return cachedStripeCustomerId;
    if (fromContext) return fromContext;

    // IMPORTANT: createCustomer should NOT require a paymentMethodId anymore
    const resp = await createCustomer({
      email,
      userId,
      name,
      stripeAccountId: coachAccountId,
    });

    if (!resp?.stripeCustomerId) {
      throw new Error("Failed to create Stripe customer.");
    }
    setCachedStripeCustomerId(resp.stripeCustomerId);
    return resp.stripeCustomerId;
  }

  async function confirmSetupAndGetPaymentMethodId(stripeCustomerIdToUse) {
    // If user chose a saved payment method, use it.
    if (!useNewPaymentMethod && selectedPaymentMethodId) {
      return selectedPaymentMethodId;
    }

    // New card path: run SetupIntent + confirmCardSetup to get a PM id.
    const card = elements.getElement(CardElement);
    if (!card) throw new Error("Card form not ready.");

    const { client_secret: clientSecret } = await createSetupIntent({
      stripeCustomerId: stripeCustomerIdToUse,
      stripeAccountId: coachAccountId,
    });

    if (!clientSecret) throw new Error("Failed to start card setup.");

    const { error: setupError, setupIntent } = await stripe.confirmCardSetup(
      clientSecret,
      {
        payment_method: {
          card,
          billing_details: { email },
        },
      }
    );

    if (setupError) throw new Error(normalizeStripeError(setupError));
    if (!setupIntent?.payment_method)
      throw new Error("Card setup did not return a payment method.");

    return setupIntent.payment_method;
  }

  async function payOneTimeItem(stripeCustomerIdToUse, product) {
    // Create PI server-side (in correct account), confirm client-side (so PM stays in correct account)
    const card = elements.getElement(CardElement);

    const { client_secret: clientSecret } = await createOneTimePaymentIntent({
      oneTimePriceId: product.priceId,
      stripeCustomerId: stripeCustomerIdToUse,
      stripeAccountId: coachAccountId,
    });

    if (!clientSecret) {
      return {
        status: "error",
        priceId: product.priceId,
        error: { message: "Failed to create payment intent." },
      };
    }

    // If user selected saved PM, confirm using payment_method: selectedPaymentMethodId
    // Else confirm using the CardElement
    const confirmParams =
      !useNewPaymentMethod && selectedPaymentMethodId
        ? { payment_method: selectedPaymentMethodId }
        : {
            payment_method: {
              card,
              billing_details: { email },
            },
          };

    const { error: piError, paymentIntent } = await stripe.confirmCardPayment(
      clientSecret,
      confirmParams
    );

    if (piError) {
      return {
        status: "error",
        priceId: product.priceId,
        error: { message: normalizeStripeError(piError) },
      };
    }

    // You might want to verify paymentIntent.status === "succeeded"
    return {
      status: "success",
      priceId: product.priceId,
      productId: product.id,
      paymentIntentId: paymentIntent?.id,
    };
  }

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError("");

    if (!stripe || !elements) return;

    setLoading(true);
    try {
      const stripeCustomerIdToUse = await ensureCustomerId();

      const purchased = [];
      const errored = [];

      // 1) Subscription (max 1)
      if (recurringProduct) {
        const pmId = await confirmSetupAndGetPaymentMethodId(
          stripeCustomerIdToUse
        );

        const subResp = await createSubscription({
          stripeCustomerId: stripeCustomerIdToUse,
          subscriptionPriceId: recurringProduct.priceId,
          paymentMethodId: pmId,
          stripeAccountId: coachAccountId,
        });

        if (subResp?.status === "success") {
          purchased.push({
            status: "success",
            priceId: recurringProduct.priceId,
            productId: recurringProduct.id,
          });
        } else if (
          subResp?.status === "requires_action" &&
          subResp?.paymentIntentClientSecret
        ) {
          // OPTIONAL: handle SCA for first invoice if you implement it server-side
          const { error: scaError } = await stripe.confirmCardPayment(
            subResp.paymentIntentClientSecret
          );
          if (scaError) {
            errored.push({
              status: "error",
              priceId: recurringProduct.priceId,
              error: { message: normalizeStripeError(scaError) },
            });
          } else {
            purchased.push({
              status: "success",
              priceId: recurringProduct.priceId,
              productId: recurringProduct.id,
            });
          }
        } else {
          errored.push({
            status: "error",
            priceId: recurringProduct.priceId,
            error: { message: normalizeStripeError(subResp?.error) },
          });
        }
      }

      // 2) One-time purchases (N)
      // If user picked a saved PM, we can confirm with it.
      // If user picked a new card, CardElement is used for each confirmation.
      for (const product of oneTimeProducts) {
        const res = await payOneTimeItem(stripeCustomerIdToUse, product);
        if (res.status === "success")
          purchased.push({
            status: "success",
            priceId: product.priceId,
            productId: product.id,
          });
        else errored.push(res);
      }

      // 3) Persist purchased items in your DB
      if (purchased.length > 0) {
        await postUserProducts({
          items: purchased,
          type: "purchased",
          userId,
          stripeAccountId: coachAccountId,
        });
      }

      if (errored.length > 0) {
        if (onPartialSuccess && purchased.length > 0)
          onPartialSuccess(purchased);
        setError(errored[0]?.error?.message || "Payment failed.");
        setLoading(false);
        return;
      }

      onSuccess();
    } catch (e) {
      setError(normalizeStripeError(e));
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit}>
      {savedPaymentMethods?.length > 0 && (
        <Card sx={{ p: 2, mb: 2 }}>
          <Typography variant="subtitle1" sx={{ mb: 1 }}>
            Payment Method
          </Typography>

          <RadioGroup
            value={
              useNewPaymentMethod ? "new" : selectedPaymentMethodId || "new"
            }
            onChange={(e) => {
              if (e.target.value === "new") {
                setUseNewPaymentMethod(true);
              } else {
                setUseNewPaymentMethod(false);
                setSelectedPaymentMethodId(e.target.value);
              }
            }}
          >
            {savedPaymentMethods.map((pm) => (
              <FormControlLabel
                key={pm.id}
                value={pm.id}
                control={<Radio />}
                label={`${pm.card.brand.toUpperCase()} •••• ${
                  pm.card.last4
                } Exp: ${pm.card.exp_month}/${pm.card.exp_year}${
                  pm.id === defaultPaymentMethodId ? " (Default)" : ""
                }`}
              />
            ))}

            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              value="new"
              control={<Radio />}
              label="Use a new card"
            />
          </RadioGroup>
        </Card>
      )}

      {useNewPaymentMethod && (
        <Card sx={{ p: 2, mb: 2 }}>
          <CardElement options={CARD_ELEMENT_OPTIONS} />
        </Card>
      )}

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <LoadingButton
        fullWidth
        type="submit"
        disabled={!stripe || loading}
        loading={loading}
        variant="contained"
      >
        Pay Now
      </LoadingButton>
    </form>
  );
};

export default CheckoutForm;
